import { Box, Divider, Typography } from "@mui/material";
import { Formik } from "formik";

import type {
  HospitalMhlAutoRouteConfig,
  ManagedLocation,
} from "@thalamos/aspire-common";

import {
  assessingOrganisationsSaves,
  DrFinderAssessingOrganisationsPickerInner,
  useAssessingOrganisationsSelection,
  type AssessingOrganisationsApi,
} from "./DrFinderAssessingOrganisationsPicker.js";
import {
  managedLocationSchema,
  type ManagedLocationFormData,
} from "./managedLocationForm.js";
import { ManagedLocationPicker } from "./ManagedLocationPicker.js";
import {
  Button,
  renderErrorToast,
  renderSuccessToast,
  Typeahead,
} from "~/components/design-system/index.js";
import {
  DropdownFormField,
  NameAddressFormField,
  TextboxFormField,
} from "~/components/form/index.js";
import { MhlAutoRouteConfigEditor } from "~/components/MhlAutoRouteConfigEditor.js";

/** Only a local authority has assessing organisations to load. */
const noOrganisations = () => Promise.resolve([]);

/** A search box whose query and options the caller owns. */
export interface SearchField {
  query: string;
  onQueryChange: (query: string) => void;
  options: { label: string; value: string }[];
}

export interface ManagedLocationEditFormProps {
  location: ManagedLocation;
  trust: SearchField;
  supervisoryLocalAuthority: SearchField;
  mhlAutoRouteConfig: HospitalMhlAutoRouteConfig | null;
  onMhlAutoRouteConfigChange: (config: HospitalMhlAutoRouteConfig) => void;
  /** Resolves to the reason the details were refused, or null once saved. */
  saveLocation: (values: ManagedLocationFormData) => Promise<string | null>;
  assessingOrganisations: AssessingOrganisationsApi;
  /** Called whenever the details have been saved, so the list can refresh. */
  onLocationSaved: () => void;
  /** Called once everything has been saved. */
  onClose: () => void;
}

/**
 * Edits one managed location. A local authority's Dr Finder assessing
 * organisations are saved with its details by the one Save button, in the
 * order {@link assessingOrganisationsSaves} sets out.
 */
export function ManagedLocationEditForm({
  location,
  trust,
  supervisoryLocalAuthority,
  mhlAutoRouteConfig,
  onMhlAutoRouteConfigChange,
  saveLocation,
  assessingOrganisations,
  onLocationSaved,
  onClose,
}: ManagedLocationEditFormProps) {
  const { selection, setSelected } = useAssessingOrganisationsSelection(
    location.id,
    location.type === "localAuthority"
      ? assessingOrganisations.loadOrganisations
      : noOrganisations,
  );

  return (
    <Formik<ManagedLocationFormData>
      validationSchema={managedLocationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        try {
          const { removals, additions } =
            values.type === "localAuthority" &&
            location.type === "localAuthority"
              ? assessingOrganisationsSaves(selection)
              : { removals: null, additions: null };

          if (removals !== null) {
            const removalsRefusal =
              await assessingOrganisations.saveOrganisations(removals);
            if (removalsRefusal !== null) {
              renderErrorToast({ message: removalsRefusal });
              return;
            }
          }

          const locationRefusal = await saveLocation(values);
          if (locationRefusal !== null) {
            renderErrorToast({
              message:
                removals === null
                  ? locationRefusal
                  : `Organisations removed; details not saved: ${locationRefusal}`,
            });
            return;
          }
          onLocationSaved();

          if (additions !== null) {
            const additionsRefusal =
              await assessingOrganisations.saveOrganisations(additions);
            if (additionsRefusal !== null) {
              renderErrorToast({
                message: `Details saved; organisations not added: ${additionsRefusal}`,
              });
              return;
            }
          }

          renderSuccessToast({ message: "Managed location updated" });
          onClose();
        } finally {
          setSubmitting(false);
        }
      }}
      initialValues={
        {
          type: location.type,
          name: location.name,
          address: {
            address: location.address,
            postalCode: location.postalCode,
            isConfirmed: true,
          },
          odsCode: location.odsCode,
          trustId: location.trustId,
          xeroInvoicePrefix: location.xeroInvoicePrefix ?? null,
          cqcLocationId: location.cqcLocationId ?? null,
          supervisoryLocalAuthorityId:
            location.supervisoryLocalAuthorityId ?? null,
          icbId: location.icbId ?? null,
        } as ManagedLocationFormData
      }
    >
      {(formikData) => {
        const {
          values,
          setValues,
          setFieldTouched,
          errors,
          touched,
          handleBlur,
          submitForm,
          isSubmitting,
        } = formikData;

        const fieldProps = {
          validationSchema: managedLocationSchema,
          values,
          setValues,
          setFieldTouched,
          errors,
          touched,
          context: {},
          handleBlur,
        };

        return (
          <>
            <Box sx={{ mb: 1 }}>
              <DropdownFormField
                field={{
                  type: "dropdown",
                  label: "Type (*)",
                  valueField: "type",
                  textField: "typeLabel",
                  options: [
                    { label: "Hospital", value: "hospital" },
                    { label: "Local Authority", value: "localAuthority" },
                    { label: "Care Home", value: "careHome" },
                  ],
                }}
                fieldProps={fieldProps}
              />
            </Box>
            <Box sx={{ mb: 0 }}>
              <TextboxFormField
                field={{
                  type: "textbox",
                  label: "Name (*)",
                  field: "name",
                }}
                fieldProps={fieldProps}
              />
            </Box>
            <Box sx={{ mb: 1 }}>
              <NameAddressFormField
                field={{
                  type: "name-address",
                  disableName: true,
                  field: "address",
                  addressLabel: "Address (*)",
                }}
                fieldProps={fieldProps}
              />
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typeahead
                disabled={false}
                inputValue={trust.query}
                onInputChange={(e) => {
                  const value = typeof e === "string" ? e : "";
                  trust.onQueryChange(value); // local input display
                  const match = trust.options.find((t) => t.label === value);
                  void setValues({
                    ...values,
                    trustId: match ? match.value : undefined,
                  }); // only set trustId in Formik
                }}
                options={trust.options}
                name="trust"
                label="Trust (NEW OPTIONAL)"
                errorMessage={
                  errors.trustId && touched.trustId ? errors.trustId : undefined
                }
              />
            </Box>
            <Box sx={{ mb: 1 }}>
              <TextboxFormField
                field={{
                  type: "textbox",
                  label: "ODS Code (optional)",
                  field: "odsCode",
                }}
                fieldProps={fieldProps}
              />
            </Box>

            {values.type === "careHome" && (
              <>
                <Box sx={{ mb: 1 }}>
                  <TextboxFormField
                    field={{
                      type: "textbox",
                      label: "CQC Location ID (optional)",
                      field: "cqcLocationId",
                    }}
                    fieldProps={fieldProps}
                  />
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typeahead
                    disabled={false}
                    inputValue={supervisoryLocalAuthority.query}
                    onInputChange={(e) => {
                      const value = typeof e === "string" ? e : "";
                      supervisoryLocalAuthority.onQueryChange(value);
                      const match = supervisoryLocalAuthority.options.find(
                        (la) => la.label === value,
                      );
                      void setValues({
                        ...values,
                        supervisoryLocalAuthorityId: match ? match.value : null,
                      });
                    }}
                    options={supervisoryLocalAuthority.options}
                    name="supervisoryLocalAuthority"
                    label="Supervisory Local Authority (grants DoLS authorisations)"
                    errorMessage={
                      errors.supervisoryLocalAuthorityId &&
                      touched.supervisoryLocalAuthorityId
                        ? errors.supervisoryLocalAuthorityId
                        : undefined
                    }
                  />
                </Box>
              </>
            )}

            {values.type === "localAuthority" && (
              <Box sx={{ mb: 1 }}>
                <TextboxFormField
                  field={{
                    type: "textbox",
                    label:
                      "Xero Invoice Prefix (optional, defaults to DrFinder)",
                    field: "xeroInvoicePrefix",
                  }}
                  fieldProps={fieldProps}
                />
              </Box>
            )}

            {values.type === "hospital" && (
              <Box sx={{ mb: 2, mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  MHL Auto-Route Configuration
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  Configure automatic routing of forms to teams when this
                  hospital is selected in Part 1. This works in addition to
                  organisation-level auto-routing.
                </Typography>
                <MhlAutoRouteConfigEditor
                  config={mhlAutoRouteConfig}
                  onChange={(newConfig) => {
                    // When showGuestEmails=true, config is always HospitalMhlAutoRouteConfig
                    onMhlAutoRouteConfigChange(
                      newConfig as HospitalMhlAutoRouteConfig,
                    );
                  }}
                  showGuestEmails={true}
                />
              </Box>
            )}

            {values.type === "localAuthority" && (
              <ManagedLocationPicker
                locationType="icb"
                label="ICB (sets the out-of-area payment policy)"
                error={errors.icbId && touched.icbId ? errors.icbId : null}
                idField={"icbId"}
                values={values}
                setValues={setValues}
              />
            )}

            {values.type === "localAuthority" &&
              location.type === "localAuthority" && (
                <>
                  <Divider sx={{ mt: 3 }} />
                  <DrFinderAssessingOrganisationsPickerInner
                    localAuthorityId={location.id}
                    selection={selection}
                    onChange={setSelected}
                    searchOrganisations={
                      assessingOrganisations.searchOrganisations
                    }
                    authorityNameOf={assessingOrganisations.authorityNameOf}
                  />
                </>
              )}

            <Button
              label={isSubmitting ? "Saving..." : "Save"}
              disabled={isSubmitting}
              onClick={() => {
                void submitForm();
              }}
            />
          </>
        );
      }}
    </Formik>
  );
}
